import React, { useMemo } from "react";
import { map } from "lodash";
import styled from "styled-components";
import { content } from "@glossy";
import { Facet } from "@linkfluence/models/facet";
import Chip from "@ui-kit/form/chip";
import Panel from "@ui-kit/popover/panel";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { Clause } from "@radarly/models/clause";
import { getInnerClauseFromClause } from "@radarly/models/clause/getter";
import { isClauseEmpty } from "@radarly/models/clause/is";
import { refineClauseWithInnerClause } from "@radarly/models/clause/refiner";
import { FullFacet } from "@radarly/models/facets";
import { getNextClauseFromStringValue } from "./clause";

const Options = styled.div`
  display: flex;
  flex-wrap: wrap;
  > * + * {
    margin-left: 6px;
  }
`;

interface IProps {
  clause: Clause;
  facet: FullFacet;
  setClause: (clause: Clause) => void;
  hasTitle?: boolean;
  onApply: () => void;
  onCancel?: () => void;
  validateLabel?: string;
  className?: string;
}

const Description = styled.div`
  margin-bottom: 24px;
  color: ${(props) => content(props, "secondary")};
`;
const RadarlyFilterbarPanelsBoolean = ({
  setClause,
  clause,
  facet,
  hasTitle,
  onApply,
  onCancel,
  validateLabel,
  className,
}: IProps) => {
  const locale = useCurrentUserLocale();
  const onSetValue = (value: string) => {
    setClause(getNextClauseFromStringValue(facet, value));
  };
  const clauseValue = getInnerClauseFromClause(clause, facet.id as Facet);
  const value = clauseValue ? clauseValue[facet.id] : null;
  const flags = useMemo(() => {
    return map(facet.values, (value, i) => {
      return {
        id: `selected_${value.id}`,
        label: tr(locale, value.label),
        value: value.id as string,
      };
    });
  }, [facet, locale]);

  const onClear = () =>
    setClause(refineClauseWithInnerClause(clause, { [facet.id]: null }, true));
  const toggleFlag = (_value: string) => {
    if (value !== _value) {
      onSetValue(_value);
    } else {
      onSetValue(null);
    }
  };
  return (
    <Panel
      locale={locale}
      onClear={onClear}
      hasTitle={hasTitle}
      title={tr(locale, "FILTERBAR::PANEL::FILTERBY", {
        filter: facet.label,
      })}
      onApply={onApply}
      onCancel={onCancel}
      disabled={onCancel ? isClauseEmpty(clause) : false}
      validateLabel={validateLabel}
      className={className}
    >
      {facet.description && <Description>{facet.description}</Description>}
      <Options>
        {!!flags &&
          map(flags, ({ id, label, value: _value }) => (
            <Chip
              key={id}
              label={label}
              onClick={() => toggleFlag(_value)}
              status={_value === value ? "include" : "none"}
              noLinethrough
            />
          ))}
      </Options>
    </Panel>
  );
};

export default RadarlyFilterbarPanelsBoolean;
